import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';

import { useAuth } from './AuthContext';
import { useHousehold } from './HouseholdContext';
import { useHouseholdMembership } from './HouseholdMembershipContext';
import { useSampleData } from './SampleDataContext';
import {
  fetchLiabilities,
  saveLiabilities,
  type SyncedLiability,
} from '../services/liabilityService';
import type { CoreAccount } from '../types/financial';
import { subscribeDashboardRefresh } from '../services/dashboardRefreshBus';
import { localLiabilitiesOnly } from '../utils/householdFromDraft';

type LiabilityContextValue = {
  loading: boolean;
  error: string | null;
  liabilities: SyncedLiability[];
  /** CoreAccount rows for derive / net worth. */
  liabilityAccounts: CoreAccount[];
  refresh: () => Promise<void>;
  /** Push local core liabilities to the household (Mine rows). */
  syncLocalToServer: () => Promise<void>;
};

const LiabilityContext = createContext<LiabilityContextValue | null>(null);

function toCoreAccount(row: SyncedLiability): CoreAccount {
  return {
    id: row.ownerUserId ? `${row.ownerUserId}:${row.liabilityId}` : row.liabilityId,
    kind: 'liability',
    type: row.type || 'other',
    label: row.label,
    balance: row.balance,
    interestRatePct: row.interestRatePct,
    monthlyPayment: row.monthlyPayment,
  };
}

function fromCoreAccount(account: CoreAccount): {
  liabilityId: string;
  type: string;
  label: string;
  balance: number;
  interestRatePct?: number;
  monthlyPayment?: number;
} {
  return {
    liabilityId: account.id,
    type: account.type || 'other',
    label: account.label,
    balance: account.balance,
    interestRatePct: account.interestRatePct,
    monthlyPayment: account.monthlyPayment,
  };
}

export function LiabilityProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, tokens } = useAuth();
  const { isSampleMode } = useSampleData();
  const { view, viewReady } = useHouseholdMembership();
  const { core } = useHousehold();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liabilities, setLiabilities] = useState<SyncedLiability[]>([]);
  const pushedKey = useRef<string>('');
  const refreshInFlight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || isSampleMode || !tokens?.accessToken) {
      setLiabilities([]);
      setError(null);
      return;
    }
    if (!viewReady) return;
    if (refreshInFlight.current) return refreshInFlight.current;
    setLoading(true);
    setError(null);
    const run = (async () => {
      try {
        const rows = await fetchLiabilities(view);
        setLiabilities(rows);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load liabilities');
      } finally {
        setLoading(false);
        refreshInFlight.current = null;
      }
    })();
    refreshInFlight.current = run;
    return run;
  }, [isAuthenticated, isSampleMode, tokens?.accessToken, view, viewReady]);

  const syncLocalToServer = useCallback(async () => {
    if (!isAuthenticated || isSampleMode || !tokens?.accessToken || !viewReady) return;
    const local = localLiabilitiesOnly(core.accounts).filter((a) => a.balance > 0);
    if (local.length === 0) return;
    const key = local
      .map((a) => `${a.id}:${a.balance}:${a.interestRatePct ?? ''}:${a.monthlyPayment ?? ''}`)
      .sort()
      .join('|');
    if (key === pushedKey.current) return;
    try {
      const saved = await saveLiabilities(local.map(fromCoreAccount));
      pushedKey.current = key;
      // Re-fetch for active view (combined may include partner rows).
      const rows = await fetchLiabilities(view);
      setLiabilities(rows.length > 0 ? rows : saved);
    } catch {
      // ignore — local still drives Mine until API is available
    }
  }, [isAuthenticated, isSampleMode, tokens?.accessToken, core.accounts, view, viewReady]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      void refresh();
    });
  }, [refresh]);

  // Push local liabilities (loan planner / onboarding) once auth is ready.
  useEffect(() => {
    void syncLocalToServer();
  }, [syncLocalToServer]);

  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status === 'active') void refresh();
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [refresh]);

  const liabilityAccounts = useMemo(() => liabilities.map(toCoreAccount), [liabilities]);

  const value = useMemo<LiabilityContextValue>(
    () => ({
      loading,
      error,
      liabilities,
      liabilityAccounts,
      refresh,
      syncLocalToServer,
    }),
    [loading, error, liabilities, liabilityAccounts, refresh, syncLocalToServer],
  );

  return <LiabilityContext.Provider value={value}>{children}</LiabilityContext.Provider>;
}

export function useLiabilities(): LiabilityContextValue {
  const ctx = useContext(LiabilityContext);
  if (!ctx) {
    throw new Error('useLiabilities must be used within LiabilityProvider');
  }
  return ctx;
}
